// Small display helpers — dates in a terse instrument style, money/weight with tabular figures.

import type { DatetimePrecision } from '@aircargomcp/types'

export function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

export function fmtDate(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function fmtMoney(v: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v)
  } catch {
    return `${currency} ${v.toFixed(2)}`
  }
}

export function fmtNum(v: number, dp = 2): string {
  return v.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })
}

// Human label for an AwbStatus.
export const STATUS_LABEL: Record<string, string> = {
  booked: 'Booked',
  accepted: 'Accepted',
  in_transit: 'In transit',
  arrived: 'Arrived',
  available: 'Ready for pickup',
  delivered: 'Delivered',
  unknown: 'Unknown',
}

/** A time on its station's clock: the day alone for a date, day and hour otherwise, marked UTC when the zone is unknown. */
export function fmtStationTime(
  at: string | null | undefined,
  timezone: string | null | undefined,
  precision: DatetimePrecision | null | undefined,
): string {
  const d = at ? new Date(at) : null
  if (!d || Number.isNaN(d.getTime())) return '—'
  const p = clockParts(d, timezone || 'UTC')
  const day = `${p.day} ${p.month}`
  if (precision === 'date') return day
  return `${day}, ${p.hour}:${p.minute}${timezone ? '' : ' UTC'}`
}

/** Day, month, hour and minute of an instant on a zone's clock. */
function clockParts(d: Date, zone: string): Record<string, string> {
  return Object.fromEntries(
    clockOf(zone)
      .formatToParts(d)
      .map((part) => [part.type, part.value]),
  )
}

const CLOCKS = new Map<string, Intl.DateTimeFormat>()

/** One formatter per zone, built once; an unknown zone reads as UTC. */
function clockOf(zone: string): Intl.DateTimeFormat {
  const kept = CLOCKS.get(zone)
  if (kept) return kept
  const opts = {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  } as const
  let fmt: Intl.DateTimeFormat
  try {
    fmt = new Intl.DateTimeFormat('en-GB', { ...opts, timeZone: zone })
  } catch {
    fmt = new Intl.DateTimeFormat('en-GB', { ...opts, timeZone: 'UTC' })
  }
  CLOCKS.set(zone, fmt)
  return fmt
}
